import { useEffect, useState } from "react";
import { API, useAuth, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Package, CheckCircle, MapPin, Clock, Truck, KeyRound, Loader2, Phone, Camera, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RiderDashboard() {
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deliverDialog, setDeliverDialog] = useState(null);
  const [deliveryCode, setDeliveryCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [viewReadyImage, setViewReadyImage] = useState(null);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/rider/orders`, { credentials: "include" });
      if (res.ok) setOrders(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const confirmDelivery = async () => {
    if (!deliveryCode || deliveryCode.length !== 6) { toast.error("Enter a valid 6-digit code"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/rider/orders/${deliverDialog.order_id}/deliver`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ delivery_code: deliveryCode }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed");
      }
      toast.success("Order marked as delivered!");
      setDeliverDialog(null);
      setDeliveryCode("");
      fetchOrders();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const statusIcon = (status) => {
    const icons = { out_for_delivery: Truck, delivered: CheckCircle, ready: Package };
    const Icon = icons[status] || Clock;
    return <Icon size={14} />;
  };

  const statusColor = (status) => {
    const colors = { out_for_delivery: "text-blue-600 bg-blue-50", delivered: "text-green-600 bg-green-50", ready: "text-amber-600 bg-amber-50" };
    return colors[status] || "text-gray-600 bg-gray-50";
  };

  const pendingOrders = orders.filter((o) => o.status !== "delivered");
  const deliveredOrders = orders.filter((o) => o.status === "delivered");
  const displayOrders = activeTab === "pending" ? pendingOrders : deliveredOrders;

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="rider-dashboard">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">My Deliveries</h1>
        <p className="text-sm text-gray-500 mb-6">Welcome, {user?.name}</p>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
            <Truck size={20} className="text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-700">{pendingOrders.length}</p>
            <p className="text-xs text-blue-600">Active</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <CheckCircle size={20} className="text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-700">{deliveredOrders.length}</p>
            <p className="text-xs text-green-600">Completed</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-4" data-testid="rider-tabs">
          <button
            onClick={() => setActiveTab("pending")}
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${activeTab === "pending" ? "text-green-700 border-b-2 border-green-600" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="rider-tab-pending"
          >
            Pending ({pendingOrders.length})
          </button>
          <button
            onClick={() => setActiveTab("delivered")}
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${activeTab === "delivered" ? "text-green-700 border-b-2 border-green-600" : "text-gray-400 hover:text-gray-600"}`}
            data-testid="rider-tab-delivered"
          >
            Delivered ({deliveredOrders.length})
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}</div>
        ) : displayOrders.length === 0 ? (
          <div className="text-center py-16">
            <Package size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">{activeTab === "pending" ? "No pending deliveries" : "No delivered orders yet"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeTab === "pending" ? (
              displayOrders.map((order) => (
                <div key={order.order_id} className="bg-white border border-gray-100 rounded-xl p-4" data-testid={`rider-order-${order.order_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{order.order_number || order.order_id.slice(0, 12)}</p>
                      <p className="text-[10px] text-gray-400">{order.user_name} · {order.user_phone || "-"}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(order.status)}`}>
                      {statusIcon(order.status)} {order.status === "out_for_delivery" ? "In Transit" : order.status}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 mb-2 bg-gray-50 rounded-lg p-2">
                    <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600 line-clamp-2">{order.delivery_address}</p>
                  </div>
                  {order.ready_image_url && (
                    <button
                      onClick={() => setViewReadyImage(`${BACKEND_URL}${order.ready_image_url}`)}
                      className="flex items-center gap-1 mb-2 text-[11px] font-semibold text-green-600 hover:text-green-700 transition-colors"
                      data-testid={`rider-view-ready-photo-${order.order_id}`}
                    >
                      <Camera size={11} /> View Ready Photo
                    </button>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-green-600">{symbol}{Math.round(order.total_amount)}</span>
                    {order.status === "out_for_delivery" && (
                      <Button size="sm" onClick={() => { setDeliverDialog(order); setDeliveryCode(""); }} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`deliver-btn-${order.order_id}`}>
                        <KeyRound size={12} className="mr-1" /> Mark Delivered
                      </Button>
                    )}
                  </div>
                  {order.user_phone && (
                    <a href={`tel:${order.user_phone}`} className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                      <Phone size={11} /> Call Customer
                    </a>
                  )}
                </div>
              ))
            ) : (
              displayOrders.map((order) => (
                <div key={order.order_id} className="bg-gray-50 border border-gray-100 rounded-xl p-4" data-testid={`rider-order-${order.order_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-700">{order.order_number || order.order_id.slice(0, 12)}</p>
                      <p className="text-[10px] text-gray-500">{order.user_name}</p>
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle size={12} /> Delivered</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Delivery Code Dialog */}
        <Dialog open={!!deliverDialog} onOpenChange={() => setDeliverDialog(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound size={18} className="text-green-600" /> Enter Delivery Code</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-gray-500">Ask the customer for their 6-digit delivery code to confirm handover.</p>
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-[10px] text-gray-500">Order: <span className="font-semibold text-gray-700">{deliverDialog?.order_number}</span></p>
                <p className="text-sm font-medium text-gray-900" data-testid="deliver-dialog-customer-name">{deliverDialog?.user_name}</p>
                {deliverDialog?.user_phone && (
                  <a href={`tel:${deliverDialog.user_phone}`} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="deliver-dialog-customer-phone">
                    <Phone size={11} /> {deliverDialog.user_phone}
                  </a>
                )}
              </div>
              <Input
                value={deliveryCode}
                onChange={(e) => setDeliveryCode(e.target.value.toUpperCase().slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="text-center text-2xl font-mono tracking-[0.3em] h-14 text-green-400 bg-gray-900 border-gray-700 placeholder:text-gray-600"
                maxLength={6}
                data-testid="delivery-code-input"
              />
              <Button onClick={confirmDelivery} disabled={submitting || deliveryCode.length !== 6} className="w-full bg-green-700 hover:bg-green-800 text-white" data-testid="confirm-delivery-btn">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Confirm Delivery
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Ready Image Popup */}
        {viewReadyImage && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewReadyImage(null)} data-testid="ready-image-popup">
            <div className="relative max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setViewReadyImage(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg z-10" data-testid="close-ready-image-popup">
                <X size={16} className="text-gray-600" />
              </button>
              <img src={viewReadyImage} alt="Order ready" className="w-full rounded-xl shadow-2xl" data-testid="ready-image-popup-img" />
              <p className="text-center text-white text-xs mt-3 font-medium">Order Ready Photo</p>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
